/**
 * PublicPaymentStep
 *
 * Shared payment-step UI for the public registration flows
 * (PublicCampPage / PublicClinicPage / PublicEventPage). Replaces
 * the per-page CardElement + confirmCardPayment dance with a
 * PaymentElement + confirmPayment dance, which lets Stripe
 * surface saved payment methods automatically when the
 * PaymentIntent has a `customer:` attached.
 *
 * The customer attachment happens server-side after the visitor
 * authenticates (verify-member returns the membership_id, the
 * registration handler looks up the member's stripe_customer_id
 * and includes it on the PI). With that in place, PaymentElement
 * renders the saved-card list above a "Use a new card" option,
 * exactly the experience the member would get when paying from
 * inside the authenticated app.
 *
 * Anonymous (un-authenticated) registrations create a PI with no
 * customer, so PaymentElement just shows a single new-card form.
 *
 * The component owns its own <Elements> wrapper (initialized with
 * the clientSecret from the registration response) so that
 * PaymentElement reads from the right Elements context. The
 * caller — the registration page — should NOT also wrap us in an
 * <Elements>, or PaymentElement will read from the wrong context
 * and fail to find the clientSecret.
 */

import React, { useMemo, useState } from "react";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import type { Stripe, StripeElementsOptions } from "@stripe/stripe-js";
import { toast } from "react-toastify";
import { getStripePromise, getStripeLoadError } from "../../utils/stripeClient";
import { capturePaymentError } from "../../utils/sentry";

export interface PublicPaymentStepProps {
  /**
   * client_secret from either a PaymentIntent (mode='payment') or a
   * SetupIntent (mode='setup'). The Elements wrapper auto-detects
   * which one based on the secret prefix, so the same component can
   * drive both flows — the difference is which `stripe.confirm*` call
   * we make at submit.
   */
  clientSecret: string;
  /**
   * 'payment' (default) — captures the charge now via confirmPayment.
   * 'setup'             — saves the card via confirmSetup; no charge
   *                       happens at this step. Used for staff-review
   *                       bookings where the actual charge fires when
   *                       staff approve (off_session).
   */
  intentMode?: "payment" | "setup";
  /** Final amount to charge (display only — Stripe charges the PI). */
  totalAmt: number;
  /** Number of participants in this batch (for the "N × $X = total" line). */
  participantCount?: number;
  /** Unit price for the participants line (display only). */
  unitPrice?: number;
  /** Billing name + email pre-fill from the contact form. Optional. */
  billingName?: string;
  billingEmail?: string;
  /** Embed-iframe dark-mode flag (matches existing public pages). */
  isEmbedDark?: boolean;
  /**
   * Server-side post-payment confirm hook (e.g. confirmCampPayment).
   * Called BEFORE onComplete; errors are non-fatal because the
   * Stripe webhook reconciles regardless. Optional — omit for
   * flows that rely on the webhook alone (events).
   */
  onPaymentSucceeded?: (paymentIntentId: string) => Promise<void>;
  /** Move the parent page to the success step. */
  onComplete: () => void;
}

const PaymentStepInner: React.FC<PublicPaymentStepProps> = ({
  intentMode = "payment",
  totalAmt,
  participantCount,
  unitPrice,
  billingName,
  billingEmail,
  isEmbedDark,
  onPaymentSucceeded,
  onComplete,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const isSetup = intentMode === "setup";

  const handlePay = async () => {
    if (!stripe || !elements) {
      // Almost-impossible after the localStorage-race fix in
      // stripeClient.ts; surface loudly to Sentry if it ever fires.
      toast.error("Payment system is still loading. Please refresh and try again.");
      capturePaymentError(
        new Error("PublicPaymentStep: stripe/elements null on Pay click"),
        { operation: "publicPay.notReady" } as any
      );
      return;
    }

    setSubmitting(true);
    try {
      const billingPmData = billingName || billingEmail
        ? {
            payment_method_data: {
              billing_details: {
                ...(billingName ? { name: billingName } : {}),
                ...(billingEmail ? { email: billingEmail } : {}),
              },
            },
          }
        : {};

      if (isSetup) {
        // SetupIntent: save the card; no charge fires now. The
        // backend's approve endpoint creates an off_session PI later.
        const { error, setupIntent } = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: window.location.href,
            ...billingPmData,
          },
          redirect: "if_required",
        });
        if (error) {
          const { getPaymentErrorMessage } = await import("../../utils/paymentErrors");
          toast.error(getPaymentErrorMessage(error));
        } else if (setupIntent?.status === "succeeded") {
          toast.success("Card authorized — your booking is now awaiting staff review.");
          onComplete();
        } else {
          toast.info(`Setup status: ${setupIntent?.status || "unknown"}. We'll follow up.`);
          onComplete();
        }
        return;
      }

      // PaymentIntent path (Phase 2 default).
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          // Required by confirmPayment even with redirect:'if_required'.
          return_url: window.location.href,
          ...billingPmData,
        },
        redirect: "if_required",
      });

      if (error) {
        const { getPaymentErrorMessage } = await import("../../utils/paymentErrors");
        toast.error(getPaymentErrorMessage(error));
      } else if (paymentIntent?.status === "succeeded") {
        if (onPaymentSucceeded) {
          try {
            await onPaymentSucceeded(paymentIntent.id);
          } catch (e) {
            // Backend confirm endpoint failed but the charge succeeded
            // — webhook will reconcile. Don't block the user.
            // eslint-disable-next-line no-console
            console.warn("Confirm callback failed, webhook will handle:", e);
          }
        }
        toast.success("Payment successful! You're registered.");
        onComplete();
      } else if (paymentIntent?.status === "processing") {
        toast.info("Payment is processing. You'll receive a confirmation email.");
        onComplete();
      } else {
        toast.info(
          `Payment status: ${paymentIntent?.status || "unknown"}. You'll receive a confirmation email.`
        );
        onComplete();
      }
    } catch (err: unknown) {
      const { getPaymentErrorMessage } = await import("../../utils/paymentErrors");
      toast.error(getPaymentErrorMessage(err as Error));
    } finally {
      setSubmitting(false);
    }
  };

  const showParticipantsLine =
    typeof participantCount === "number" &&
    participantCount > 1 &&
    typeof unitPrice === "number";

  return (
    <div
      className={`rounded-lg border p-6 ${
        isEmbedDark
          ? "bg-gray-800 border-gray-700 embed-card"
          : "bg-white border-gray-200"
      }`}
    >
      <h3
        className={`text-xl font-semibold mb-2 ${
          isEmbedDark ? "text-gray-100" : "text-gray-900"
        }`}
      >
        {isSetup ? "Authorize your card" : "Complete Payment"}
      </h3>
      <p
        className={`mb-6 ${
          isEmbedDark ? "text-gray-400" : "text-gray-600"
        }`}
      >
        {isSetup ? (
          <>
            We'll review your booking and only charge ${totalAmt.toFixed(2)} if it's approved.
            Your card won't be charged at this step.
          </>
        ) : (
          <>
            {showParticipantsLine && (
              <span>
                {participantCount} participants × ${(unitPrice as number).toFixed(2)} ={" "}
              </span>
            )}
            Amount due:{" "}
            <span
              className={`font-bold text-lg ${
                isEmbedDark ? "text-gray-100" : "text-gray-900"
              }`}
            >
              ${totalAmt.toFixed(2)}
            </span>
          </>
        )}
      </p>

      {/* Stripe.js load error — most commonly an ad blocker / content
          blocker stripping js.stripe.com. Surface explicitly so the
          customer knows why the Pay button won't fire. */}
      {getStripeLoadError() && (
        <div className="rounded-md mb-4 p-3 bg-red-50 border border-red-200 text-sm text-red-700 dark:bg-red-900/20 dark:border-red-700 dark:text-red-200">
          {getStripeLoadError()}
        </div>
      )}

      <div className="mb-6">
        <PaymentElement
          options={{
            layout: "tabs",
            // Saved methods surface automatically when the PaymentIntent's
            // customer is attached. We don't need to pass anything else.
          }}
        />
      </div>

      <button
        onClick={handlePay}
        disabled={submitting || !stripe || !elements}
        className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-400 dark:disabled:bg-gray-600 transition-colors"
      >
        {submitting
          ? "Processing..."
          : !stripe || !elements
          ? "Loading payment system…"
          : isSetup
          ? "Authorize card"
          : `Pay $${totalAmt.toFixed(2)}`}
      </button>
    </div>
  );
};

const PublicPaymentStep: React.FC<PublicPaymentStepProps> = (props) => {
  const stripePromise: Promise<Stripe | null> = useMemo(
    () => getStripePromise(),
    []
  );

  // Re-init Elements whenever the clientSecret changes. The
  // appearance theme is keyed off the embed dark-mode flag so the
  // PaymentElement matches the rest of the page in iframes.
  const options = useMemo<StripeElementsOptions>(
    () => ({
      clientSecret: props.clientSecret,
      appearance: {
        theme: props.isEmbedDark ? "night" : "stripe",
      },
    }),
    [props.clientSecret, props.isEmbedDark]
  );

  return (
    <Elements stripe={stripePromise} options={options}>
      <PaymentStepInner {...props} />
    </Elements>
  );
};

export default PublicPaymentStep;
